import { useCallback, useEffect, useState, type DragEvent } from 'react';
import { MEDIA_PANEL_DRAG_MIME } from '@/utils/mediaPanelDrag.utils';
import type { InputSlot } from './inputRules';
import type { InputTarget } from './useComposerInputs';

/** Files from the computer, or a result dragged from the gallery. */
export const isMediaDrag = (dataTransfer: DataTransfer): boolean =>
  dataTransfer.types.includes('Files') || dataTransfer.types.includes(MEDIA_PANEL_DRAG_MIME);

/**
 * Whether a drag is over an element. Safari sends `dragleave` with no `relatedTarget` when the pointer moves onto
 * a child, and a cancelled drag may send no `dragleave` at all, so it also clears when any drag ends or drops.
 */
export function useDragHighlight() {
  const [over, setOver] = useState(false);

  useEffect(() => {
    if (!over) {
      return;
    }
    const clear = () => setOver(false);
    window.addEventListener('drop', clear);
    window.addEventListener('dragend', clear);
    return () => {
      window.removeEventListener('drop', clear);
      window.removeEventListener('dragend', clear);
    };
  }, [over]);

  const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) {
      setOver(false);
    }
  }, []);

  return { over, setOver, onDragLeave };
}

/**
 * A file dropped outside the prompt box would make the browser open it in place of the page, and the prompt and
 * inputs would be lost. Drops the page doesn't take are refused instead.
 */
export function usePageFileDropGuard() {
  useEffect(() => {
    const refuse = (event: globalThis.DragEvent) => {
      if (event.defaultPrevented || !event.dataTransfer?.types.includes('Files')) {
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = 'none';
    };
    window.addEventListener('dragover', refuse);
    window.addEventListener('drop', refuse);
    return () => {
      window.removeEventListener('dragover', refuse);
      window.removeEventListener('drop', refuse);
    };
  }, []);
}

/**
 * Lets one slot take a drop, so the user picks where it goes. The box around it sees the drop as already taken,
 * since this one calls `preventDefault`.
 */
export function useSlotDrop(
  slot: InputSlot,
  onDropInto: ((dataTransfer: DataTransfer, target: InputTarget) => void) | undefined,
) {
  const { over, setOver, onDragLeave } = useDragHighlight();

  const onDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!onDropInto || !isMediaDrag(event.dataTransfer)) {
        return;
      }
      event.preventDefault();
      setOver(true);
    },
    [onDropInto, setOver],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!onDropInto || !isMediaDrag(event.dataTransfer)) {
        return;
      }
      event.preventDefault();
      setOver(false);
      onDropInto(event.dataTransfer, slot);
    },
    [onDropInto, setOver, slot],
  );

  return { over, dropProps: { onDragOver, onDragLeave, onDrop } };
}
